import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { LOGO_URL, euro } from "../lib/constants";
import {
    Shield,
    LogOut,
    Users,
    Activity,
    Search,
    AlertTriangle,
    UserX,
    UserCheck,
    Crown,
    KeyRound,
    Trash2,
    RefreshCw,
    ScrollText,
    X,
    Lock,
    Zap,
    Infinity as InfinityIcon,
    Sparkles,
    DollarSign,
    Tag,
    Save,
    Plus,
    Calendar,
} from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
    const { user, loading } = useAuth();
    if (loading) {
        return <div className="min-h-screen bg-brand-navy flex items-center justify-center text-white/70">Caricamento…</div>;
    }
    if (!user) return <AdminLogin />;
    if (!user.is_admin) return <Navigate to="/dashboard" replace />;
    return <AdminConsole />;
}

function AdminLogin() {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await login(email, password);
            if (!u?.is_admin) {
                setErr("Account non autorizzato all'area amministrativa.");
                setLoading(false);
                return;
            }
            // ricaricato da useAuth → la pagina rerender e mostra console
        } catch (e2) {
            setErr(formatApiError(e2));
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-brand-navy bg-paper-grain flex items-center justify-center px-5">
            <form
                onSubmit={submit}
                className="w-full max-w-md bg-paper rounded-2xl border border-brand-line shadow-card p-7"
                data-testid="admin-login-form"
            >
                <div className="flex items-center gap-3 mb-6">
                    <div className="h-11 w-11 rounded-full bg-brand-navy text-brand-gold flex items-center justify-center">
                        <Shield size={20} />
                    </div>
                    <div>
                        <div className="eyebrow text-brand-gold">Area riservata</div>
                        <h1 className="font-display text-2xl text-brand-ink">Console amministrativa</h1>
                    </div>
                </div>

                <label className="block text-[11px] uppercase tracking-[0.18em] text-brand-muted mb-1">Email amministratore</label>
                <input
                    type="email"
                    autoFocus
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold mb-4"
                    data-testid="admin-email"
                    required
                />

                <label className="block text-[11px] uppercase tracking-[0.18em] text-brand-muted mb-1">Password</label>
                <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold mb-4"
                    data-testid="admin-password"
                    required
                />

                {err && (
                    <div className="mb-3 text-sm text-brand-danger flex items-center gap-2">
                        <AlertTriangle size={14} /> {err}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading}
                    data-testid="admin-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy text-white py-2.5 text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-60"
                >
                    <Lock size={14} /> {loading ? "Accesso…" : "Accedi"}
                </button>
            </form>
        </div>
    );
}

function AdminConsole() {
    const { logout, user } = useAuth();
    const [tab, setTab] = useState("overview");
    return (
        <div className="min-h-screen bg-brand-cream bg-cream-grain">
            <header className="bg-brand-navy text-white border-b border-black/30">
                <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-4 flex items-center gap-4">
                    <img src={LOGO_URL} alt="" className="h-9 w-9" />
                    <div className="flex-1">
                        <div className="eyebrow text-brand-gold">Admin Console</div>
                        <div className="font-display text-xl">RENOVA360</div>
                    </div>
                    <div className="hidden sm:block text-right">
                        <div className="text-[12px] text-white/60">Connesso come</div>
                        <div className="text-sm">{user?.email}</div>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="admin-logout"
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm hover:bg-white/10 transition"
                    >
                        <LogOut size={14} /> Esci
                    </button>
                </div>
                <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 flex gap-1 overflow-x-auto">
                    <TabButton active={tab === "overview"} onClick={() => setTab("overview")} icon={<Activity size={14} />} testid="admin-tab-overview">
                        Panoramica
                    </TabButton>
                    <TabButton active={tab === "users"} onClick={() => setTab("users")} icon={<Users size={14} />} testid="admin-tab-users">
                        Utenti
                    </TabButton>
                    <TabButton active={tab === "pricing"} onClick={() => setTab("pricing")} icon={<DollarSign size={14} />} testid="admin-tab-pricing">
                        Prezzi
                    </TabButton>
                    <TabButton active={tab === "promos"} onClick={() => setTab("promos")} icon={<Tag size={14} />} testid="admin-tab-promos">
                        Promo
                    </TabButton>
                    <TabButton active={tab === "logs"} onClick={() => setTab("logs")} icon={<ScrollText size={14} />} testid="admin-tab-logs">
                        Audit log
                    </TabButton>
                </nav>
            </header>

            <main className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8">
                {tab === "overview" && <Overview />}
                {tab === "users" && <UsersList />}
                {tab === "pricing" && <PricingPanel />}
                {tab === "promos" && <PromosPanel />}
                {tab === "logs" && <AuditLog />}
            </main>
        </div>
    );
}

function TabButton({ active, onClick, icon, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm rounded-t-lg transition ${
                active ? "bg-brand-cream text-brand-ink font-semibold" : "text-white/70 hover:text-white"
            }`}
        >
            {icon} {children}
        </button>
    );
}

function Overview() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/overview");
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading || !data) return <div className="py-12 text-center text-brand-muted">Caricamento…</div>;

    return (
        <div className="space-y-7" data-testid="admin-overview">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl text-brand-ink">Panoramica</h2>
                <button onClick={load} className="text-sm text-brand-muted hover:text-brand-ink inline-flex items-center gap-1.5">
                    <RefreshCw size={14} /> Aggiorna
                </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="Utenti totali" value={data.users_total} accent="#1a2a44" />
                <Kpi label="Pro paganti" value={data.users_paying_pro} accent="#c9a961" />
                <Kpi label="Base paganti" value={data.users_paying_base} accent="#1a2a44" />
                <Kpi label="In trial" value={data.users_trialing} accent="#0288d1" />
                <Kpi label="Scaduti" value={data.users_expired} accent="#d32f2f" />
                <Kpi label="Sospesi" value={data.users_suspended} accent="#9e9e9e" />
                <Kpi label="MRR stimato" value={euro(data.mrr_eur || 0)} accent="#2e7d32" big />
                <Kpi label="Ingredienti totali" value={data.ingredients_total} accent="#1a2a44" />
            </div>

            <div className="bg-paper rounded-xl border border-brand-line shadow-card p-5">
                <h3 className="font-display text-lg text-brand-ink mb-3">Ultime azioni admin</h3>
                {data.recent_audit?.length === 0 ? (
                    <div className="text-sm text-brand-muted">Nessuna azione registrata.</div>
                ) : (
                    <div className="space-y-2">
                        {data.recent_audit.map((a) => (
                            <div key={a.id} className="flex items-baseline justify-between text-sm border-b border-brand-line pb-1.5 last:border-0">
                                <div className="text-brand-ink">{actionLabel(a.action)}</div>
                                <div className="text-[12px] text-brand-muted">{new Date(a.at).toLocaleString("it-IT")}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Kpi({ label, value, accent, big }) {
    return (
        <div className={`bg-paper rounded-xl border border-brand-line shadow-card p-4 ${big ? "col-span-2" : ""}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">{label}</div>
            <div className="font-display text-3xl mt-1.5" style={{ color: accent }}>{value}</div>
        </div>
    );
}

function UsersList() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [planFilter, setPlanFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (planFilter) params.plan = planFilter;
            if (statusFilter) params.status = statusFilter;
            const { data } = await api.get("/admin/users", { params });
            setUsers(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [planFilter, statusFilter]);

    return (
        <div className="space-y-5" data-testid="admin-users">
            <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display text-2xl text-brand-ink flex-1">Utenti ({users.length})</h2>
            </div>

            <div className="bg-paper rounded-xl border border-brand-line shadow-card p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative sm:col-span-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca per email, nome…"
                        className="w-full pl-9 pr-3 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        data-testid="admin-users-search"
                    />
                </form>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 rounded-md bg-white border border-brand-line text-sm"
                    data-testid="admin-users-status"
                >
                    <option value="">Tutti gli stati</option>
                    <option value="active">Attivi (paganti)</option>
                    <option value="trialing">In prova</option>
                    <option value="expired">Scaduti</option>
                    <option value="suspended">Sospesi</option>
                </select>
                <select
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                    className="px-3 py-2 rounded-md bg-white border border-brand-line text-sm"
                    data-testid="admin-users-plan"
                >
                    <option value="">Tutti i piani</option>
                    <option value="base">Base</option>
                    <option value="pro">Pro</option>
                </select>
            </div>

            {loading ? (
                <div className="py-12 text-center text-brand-muted">Caricamento…</div>
            ) : users.length === 0 ? (
                <div className="py-12 text-center text-sm text-brand-muted">Nessun utente.</div>
            ) : (
                <div className="bg-paper rounded-xl border border-brand-line shadow-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-brand-cream text-brand-muted text-[11px] uppercase tracking-[0.16em]">
                                <tr>
                                    <th className="text-left px-4 py-3">Utente</th>
                                    <th className="text-left px-4 py-3">Stato</th>
                                    <th className="text-left px-4 py-3">Piano</th>
                                    <th className="text-right px-4 py-3">Giorni</th>
                                    <th className="text-right px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-brand-line">
                                {users.map((u) => (
                                    <UserRow key={u.id} user={u} onOpen={() => setSelected(u.id)} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {selected && (
                <UserDetailDialog
                    userId={selected}
                    onClose={() => setSelected(null)}
                    onChanged={load}
                />
            )}
        </div>
    );
}

function UserRow({ user, onOpen }) {
    const status = user.suspended ? "suspended" : user.subscription_status;
    const colors = {
        active: { bg: "rgba(46,125,50,0.12)", c: "#2e7d32" },
        trialing: { bg: "rgba(2,136,209,0.12)", c: "#0288d1" },
        expired: { bg: "rgba(211,47,47,0.12)", c: "#d32f2f" },
        suspended: { bg: "rgba(158,158,158,0.18)", c: "#616161" },
    };
    const c = colors[status] || colors.expired;
    return (
        <tr className="hover:bg-brand-cream/50" data-testid={`admin-user-row-${user.id}`}>
            <td className="px-4 py-3">
                <div className="font-semibold text-brand-ink truncate">{user.email}</div>
                <div className="text-[11px] text-brand-muted">{user.restaurant_name || user.name}</div>
            </td>
            <td className="px-4 py-3">
                <span
                    className="inline-block text-[10px] uppercase font-bold tracking-[0.16em] px-2 py-0.5 rounded-full"
                    style={{ background: c.bg, color: c.c }}
                >
                    {labelStatus(status)}
                </span>
            </td>
            <td className="px-4 py-3">
                <span className="text-brand-ink">{user.plan_tier === "pro" ? "Pro" : "Base"}</span>
                {user.admin_grant_pro && (
                    <span className="ml-2 text-[10px] uppercase font-bold text-brand-gold">REGALATO</span>
                )}
                {user.unlimited_ai && (
                    <span
                        className="ml-2 inline-flex items-center gap-0.5 text-[10px] uppercase font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5"
                        title="AI illimitata"
                    >
                        <InfinityIcon size={10} strokeWidth={3} /> AI
                    </span>
                )}
            </td>
            <td className="px-4 py-3 text-right text-brand-ink">{user.days_left}gg</td>
            <td className="px-4 py-3 text-right">
                <button
                    onClick={onOpen}
                    data-testid={`admin-user-open-${user.id}`}
                    className="text-[12px] text-brand-navy font-semibold hover:underline"
                >
                    Gestisci →
                </button>
            </td>
        </tr>
    );
}

function UserDetailDialog({ userId, onClose, onChanged }) {
    const [user, setUser] = useState(null);
    const [busy, setBusy] = useState("");
    const [tempPwd, setTempPwd] = useState("");
    const [planTier, setPlanTier] = useState("base");
    const [planDays, setPlanDays] = useState(30);
    const [grantFree, setGrantFree] = useState(false);
    const [trialDays, setTrialDays] = useState(7);
    const [grantInvoices, setGrantInvoices] = useState(0);
    const [grantMenus, setGrantMenus] = useState(0);
    const [grantRecipes, setGrantRecipes] = useState(0);

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/users/${userId}`);
            setUser(data);
            setPlanTier(data.billing?.plan_tier || "base");
        } catch (e) {
            toast.error(formatApiError(e));
            onClose();
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

    const action = async (label, fn) => {
        setBusy(label);
        try {
            await fn();
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy("");
        }
    };

    const toggleSuspend = () =>
        action("suspend", async () => {
            await api.post(`/admin/users/${userId}/suspend`, { suspended: !user.suspended });
            toast.success(user.suspended ? "Account riattivato" : "Account sospeso");
        });

    const setPlan = () =>
        action("plan", async () => {
            await api.post(`/admin/users/${userId}/plan`, {
                plan_tier: planTier,
                days: parseInt(planDays) || 30,
                grant_free: grantFree,
            });
            toast.success(grantFree ? "Piano regalato all'utente" : "Piano aggiornato");
        });

    const extendTrial = () =>
        action("trial", async () => {
            await api.post(`/admin/users/${userId}/extend-trial`, {
                days: parseInt(trialDays) || 7,
            });
            toast.success(`Trial esteso a ${trialDays} giorni`);
        });

    const grantQuota = () =>
        action("quota", async () => {
            const inv = parseInt(grantInvoices) || 0;
            const men = parseInt(grantMenus) || 0;
            const rec = parseInt(grantRecipes) || 0;
            if (inv === 0 && men === 0 && rec === 0) {
                toast.error("Inserisci almeno un valore non zero");
                throw new Error("no-op");
            }
            await api.post(`/admin/users/${userId}/quota-grant`, {
                invoices: inv,
                menus: men,
                recipes: rec,
            });
            const parts = [];
            if (inv) parts.push(`${inv > 0 ? "+" : ""}${inv} fatture`);
            if (men) parts.push(`${men > 0 ? "+" : ""}${men} menu`);
            if (rec) parts.push(`${rec > 0 ? "+" : ""}${rec} ricette AI`);
            toast.success(`Upload regalati: ${parts.join(", ")}`);
            setGrantInvoices(0);
            setGrantMenus(0);
            setGrantRecipes(0);
        });

    const toggleUnlimited = () =>
        action("unlimited", async () => {
            const next = !user.unlimited_ai;
            await api.post(`/admin/users/${userId}/unlimited`, { unlimited: next });
            toast.success(next ? "AI illimitata attivata" : "AI illimitata disattivata");
        });

    const resetPassword = () =>
        action("password", async () => {
            const { data } = await api.post(`/admin/users/${userId}/reset-password`);
            setTempPwd(data.temporary_password);
            toast.success("Password resettata. Comunicala all'utente.");
        });

    const deleteUser = () => {
        if (!window.confirm(`Cancellare definitivamente ${user.email}? Verranno eliminati anche tutti i suoi ingredienti, ricette e acquisti.`)) return;
        action("delete", async () => {
            await api.delete(`/admin/users/${userId}`);
            toast.success("Utente cancellato");
            onClose();
            onChanged?.();
        });
    };

    if (!user) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-5" onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-brand-cream w-full max-w-3xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto shadow-card"
                data-testid="admin-user-dialog"
            >
                <div className="sticky top-0 bg-brand-navy text-white px-5 py-4 flex items-center justify-between">
                    <div className="min-w-0">
                        <div className="eyebrow text-brand-gold">Utente</div>
                        <div className="font-display text-xl truncate">{user.email}</div>
                    </div>
                    <button onClick={onClose} aria-label="Chiudi" className="text-white/70 hover:text-white">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {tempPwd && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                            <div className="text-[11px] uppercase font-bold text-emerald-800 mb-1">Password temporanea generata</div>
                            <code className="block bg-white border border-emerald-200 rounded px-3 py-2 text-lg font-mono text-emerald-900 select-all">
                                {tempPwd}
                            </code>
                            <div className="mt-2 text-[12px] text-emerald-800">Comunicala all'utente in modo sicuro. Verrà richiesto di cambiarla al prossimo accesso.</div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <Stat label="Stato" value={labelStatus(user.suspended ? "suspended" : user.billing.subscription_status)} />
                        <Stat label="Piano" value={user.billing.plan_tier === "pro" ? "Pro" : "Base"} />
                        <Stat label="Giorni residui" value={`${user.billing.days_left}gg`} />
                        <Stat label="Provider" value={user.provider || "password"} />
                        <Stat label="Ingredienti" value={user.counts?.ingredients} />
                        <Stat label="Ricette" value={user.counts?.recipes} />
                        <Stat label="Acquisti" value={user.counts?.purchases} />
                        <Stat label="Fornitori" value={user.counts?.suppliers} />
                    </div>

                    <Section
                        title="Dati di fatturazione"
                        tone={user.billing_info_complete ? "" : "warning"}
                    >
                        {user.billing_info_complete ? (
                            <dl
                                className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]"
                                data-testid="admin-billing-info"
                            >
                                <BillingRow
                                    label="Ragione sociale / Nome e Cognome"
                                    value={user.billing_company_name}
                                />
                                <BillingRow
                                    label="P.IVA / Codice Fiscale"
                                    value={user.billing_vat_or_cf}
                                />
                                <BillingRow
                                    label="Indirizzo"
                                    value={user.billing_address}
                                />
                                <BillingRow
                                    label="Email fatturazione"
                                    value={user.billing_email}
                                />
                                <BillingRow
                                    label="PEC / SDI"
                                    value={user.billing_pec_or_sdi}
                                />
                                <BillingRow
                                    label="Telefono"
                                    value={user.billing_phone}
                                />
                            </dl>
                        ) : (
                            <p
                                className="text-[12px] text-amber-800"
                                data-testid="admin-billing-info-missing"
                            >
                                L'utente non ha ancora compilato i dati di
                                fatturazione. Saranno richiesti al primo
                                acquisto di un piano a pagamento.
                            </p>
                        )}
                    </Section>

                    <Section title="Sospensione account" tone={user.suspended ? "warning" : ""}>
                        <p className="text-sm text-brand-muted mb-3">
                            {user.suspended
                                ? "L'account è sospeso. L'utente non può accedere a nessuna funzione."
                                : "Sospendere blocca subito l'accesso (mantiene i dati)."}
                        </p>
                        <button
                            onClick={toggleSuspend}
                            disabled={busy === "suspend"}
                            data-testid="admin-suspend-btn"
                            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition disabled:opacity-60 ${
                                user.suspended
                                    ? "bg-brand-success text-white"
                                    : "bg-brand-navy text-white"
                            }`}
                        >
                            {user.suspended ? <UserCheck size={14} /> : <UserX size={14} />}
                            {user.suspended ? "Riattiva" : "Sospendi"}
                        </button>
                    </Section>

                    <Section title="Piano e abbonamento">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">Piano</label>
                                <select
                                    value={planTier}
                                    onChange={(e) => setPlanTier(e.target.value)}
                                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm"
                                >
                                    <option value="base">Base (29€/mese)</option>
                                    <option value="pro">Pro (49€/mese)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">Aggiungi giorni</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={planDays}
                                    onChange={(e) => setPlanDays(e.target.value)}
                                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm"
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm text-brand-ink mt-5">
                                <input
                                    type="checkbox"
                                    checked={grantFree}
                                    onChange={(e) => setGrantFree(e.target.checked)}
                                    data-testid="admin-grant-free"
                                />
                                Regala gratis (no Stripe)
                            </label>
                        </div>
                        <button
                            onClick={setPlan}
                            disabled={busy === "plan"}
                            data-testid="admin-plan-btn"
                            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-brand-gold text-brand-navy hover:bg-brand-goldDark transition disabled:opacity-60"
                        >
                            <Crown size={14} /> Imposta piano
                        </button>
                    </Section>

                    <Section title="Estendi trial">
                        <div className="flex items-end gap-3">
                            <div className="flex-1">
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">Giorni totali da oggi</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={trialDays}
                                    onChange={(e) => setTrialDays(e.target.value)}
                                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm"
                                />
                            </div>
                            <button
                                onClick={extendTrial}
                                disabled={busy === "trial"}
                                data-testid="admin-trial-btn"
                                className="rounded-full bg-brand-navy text-white px-5 py-2 text-sm font-semibold hover:bg-brand-navyLight disabled:opacity-60"
                            >
                                Estendi
                            </button>
                        </div>
                    </Section>

                    <Section title="Uso AI illimitato" tone={user.unlimited_ai ? "success" : ""}>
                        <div className="flex items-start gap-3 mb-3">
                            <div className="h-10 w-10 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center shrink-0">
                                <InfinityIcon size={18} />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm text-brand-ink">
                                    {user.unlimited_ai
                                        ? "L'utente ha accesso illimitato a fatture OCR, import menu PDF e ricette AI. Nessun consumo quota."
                                        : "Attiva per permettere a questo utente di usare AI e upload senza limiti mensili (bypassa Base/Pro)."}
                                </p>
                                <p className="mt-1 text-[12px] text-brand-muted">
                                    Consigliato per VIP, beta-tester, account demo. Il flag si può disattivare in qualsiasi momento.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={toggleUnlimited}
                            disabled={busy === "unlimited"}
                            data-testid="admin-unlimited-btn"
                            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition disabled:opacity-60 ${
                                user.unlimited_ai
                                    ? "bg-white text-brand-navy border border-brand-line hover:bg-brand-cream"
                                    : "bg-brand-gold text-brand-navy hover:bg-brand-goldDark shadow-gold"
                            }`}
                        >
                            <InfinityIcon size={14} />
                            {user.unlimited_ai ? "Disattiva illimitato" : "Attiva illimitato"}
                        </button>
                    </Section>

                    <Section title="Regala upload AI">
                        <p className="text-sm text-brand-muted mb-3">
                            Aggiungi crediti permanenti agli extra dell'utente (non scadono finché non vengono consumati). Valori negativi sottraggono.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                                    Fatture OCR (ora: {user.extra_invoices || 0})
                                </label>
                                <input
                                    type="number"
                                    value={grantInvoices}
                                    onChange={(e) => setGrantInvoices(e.target.value)}
                                    placeholder="+20"
                                    data-testid="admin-grant-invoices"
                                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                                    Menu PDF (ora: {user.extra_menus || 0})
                                </label>
                                <input
                                    type="number"
                                    value={grantMenus}
                                    onChange={(e) => setGrantMenus(e.target.value)}
                                    placeholder="+5"
                                    data-testid="admin-grant-menus"
                                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                                    Ricette AI (ora: {user.extra_recipes || 0})
                                </label>
                                <input
                                    type="number"
                                    value={grantRecipes}
                                    onChange={(e) => setGrantRecipes(e.target.value)}
                                    placeholder="+10"
                                    data-testid="admin-grant-recipes"
                                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm"
                                />
                            </div>
                        </div>
                        <button
                            onClick={grantQuota}
                            disabled={busy === "quota"}
                            data-testid="admin-grant-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-5 py-2 text-sm font-bold hover:bg-brand-goldDark transition disabled:opacity-60 shadow-gold"
                        >
                            <Zap size={14} /> Applica regalo
                        </button>
                    </Section>

                    <Section title="Reset password">
                        <p className="text-sm text-brand-muted mb-3">
                            Genera una password temporanea da comunicare all'utente.
                        </p>
                        <button
                            onClick={resetPassword}
                            disabled={busy === "password"}
                            data-testid="admin-reset-pwd-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2 text-sm font-semibold hover:bg-brand-navyLight disabled:opacity-60"
                        >
                            <KeyRound size={14} /> Genera password
                        </button>
                    </Section>

                    <Section title="Cancellazione" tone="danger">
                        <p className="text-sm text-brand-muted mb-3">
                            Eliminerà l'utente e <strong>tutti i suoi dati</strong> (ingredienti, ricette, acquisti, fornitori).
                            Operazione irreversibile.
                        </p>
                        <button
                            onClick={deleteUser}
                            disabled={busy === "delete"}
                            data-testid="admin-delete-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-danger text-white px-5 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                        >
                            <Trash2 size={14} /> Cancella utente
                        </button>
                    </Section>
                </div>
            </div>
        </div>
    );
}

function Section({ title, tone, children }) {
    const cls = tone === "danger"
        ? "border-red-200 bg-red-50"
        : tone === "warning"
            ? "border-amber-200 bg-amber-50"
            : tone === "success"
                ? "border-emerald-200 bg-emerald-50"
                : "border-brand-line bg-paper";
    return (
        <div className={`rounded-xl border p-4 ${cls}`}>
            <h4 className="font-display text-lg text-brand-ink mb-2">{title}</h4>
            {children}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-paper border border-brand-line rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted">{label}</div>
            <div className="text-sm font-semibold text-brand-ink">{value ?? "—"}</div>
        </div>
    );
}

function BillingRow({ label, value }) {
    return (
        <div className="flex justify-between gap-3">
            <dt className="text-brand-muted truncate">{label}</dt>
            <dd
                className="text-brand-ink font-semibold text-right truncate max-w-[60%]"
                title={value || ""}
            >
                {value || "—"}
            </dd>
        </div>
    );
}

function AuditLog() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/admin/audit-log")
            .then(({ data }) => setItems(data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="admin-audit">
            <h2 className="font-display text-2xl text-brand-ink mb-5">Audit log</h2>
            {loading ? (
                <div className="py-12 text-center text-brand-muted">Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="py-12 text-center text-sm text-brand-muted">Nessuna azione registrata.</div>
            ) : (
                <div className="bg-paper rounded-xl border border-brand-line shadow-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-cream text-[11px] uppercase tracking-[0.16em] text-brand-muted">
                            <tr>
                                <th className="text-left px-4 py-3">Data</th>
                                <th className="text-left px-4 py-3">Azione</th>
                                <th className="text-left px-4 py-3">Utente target</th>
                                <th className="text-left px-4 py-3">Dettagli</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-line">
                            {items.map((it) => (
                                <tr key={it.id}>
                                    <td className="px-4 py-3 text-brand-muted">{new Date(it.at).toLocaleString("it-IT")}</td>
                                    <td className="px-4 py-3 text-brand-ink font-semibold">{actionLabel(it.action)}</td>
                                    <td className="px-4 py-3 text-brand-muted">{it.target_email || "—"}</td>
                                    <td className="px-4 py-3 text-[12px] text-brand-muted">
                                        {Object.entries(it.meta || {}).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(" · ")}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function labelStatus(s) {
    return {
        active: "Attivo",
        trialing: "In prova",
        expired: "Scaduto",
        suspended: "Sospeso",
    }[s] || s;
}

function actionLabel(a) {
    return {
        suspend_user: "Sospensione/riattivazione utente",
        set_plan: "Cambio piano",
        extend_trial: "Estensione trial",
        reset_password: "Reset password",
        delete_user: "Cancellazione utente",
        grant_quota: "Regalo upload AI",
        set_unlimited: "Toggle AI illimitata",
        update_prices: "Modifica prezzi piattaforma",
        create_promo: "Creazione promo",
        update_promo: "Modifica promo",
        delete_promo: "Eliminazione promo",
    }[a] || a;
}


// ---------------------------------------------------------------------------
// PRICING PANEL — modifica prezzi Base/Pro/Top-up
// ---------------------------------------------------------------------------
function PricingPanel() {
    const [data, setData] = useState(null);
    const [base, setBase] = useState("");
    const [pro, setPro] = useState("");
    const [topup, setTopup] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const res = await api.get("/admin/platform-settings");
            setData(res.data);
            setBase(String(res.data.prices.base));
            setPro(String(res.data.prices.pro));
            setTopup(String(res.data.prices.topup));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            const b = parseFloat(base), p = parseFloat(pro), t = parseFloat(topup);
            if (!(b >= 1 && p >= 1 && t >= 1)) {
                toast.error("Tutti i prezzi devono essere ≥ 1€");
                setBusy(false);
                return;
            }
            await api.put("/admin/platform-settings/prices", { base: b, pro: p, topup: t });
            toast.success("Prezzi aggiornati con successo");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const resetDefault = () => {
        if (!data?.default_prices) return;
        setBase(String(data.default_prices.base));
        setPro(String(data.default_prices.pro));
        setTopup(String(data.default_prices.topup));
    };

    if (!data) return <div className="text-sm text-brand-muted">Caricamento…</div>;

    return (
        <div className="max-w-2xl space-y-6" data-testid="admin-pricing-panel">
            <div>
                <h2 className="font-display text-3xl text-brand-ink">Prezzi della piattaforma</h2>
                <p className="text-[13px] text-brand-muted mt-1">
                    Le modifiche sono effettive immediatamente sul checkout Stripe.
                    Default: Base 29€ · Pro 49€ · Top-up 10€.
                </p>
            </div>
            <div className="rounded-2xl border border-brand-line bg-paper shadow-card p-6 space-y-4">
                <PriceField label="Base (€/mese)" value={base} onChange={setBase} testid="price-base" />
                <PriceField label="Pro (€/mese)" value={pro} onChange={setPro} testid="price-pro" />
                <PriceField label="Top-up (€ una tantum)" value={topup} onChange={setTopup} testid="price-topup" />
                <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button
                        onClick={save}
                        disabled={busy}
                        data-testid="price-save-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-6 py-2.5 text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-50"
                    >
                        <Save size={14} /> Salva prezzi
                    </button>
                    <button
                        onClick={resetDefault}
                        disabled={busy}
                        className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-paper text-sm font-semibold px-5 py-2.5 hover:bg-brand-cream"
                    >
                        Ripristina default
                    </button>
                </div>
            </div>
        </div>
    );
}

function PriceField({ label, value, onChange, testid }) {
    return (
        <div className="flex items-center gap-4">
            <label className="w-44 text-[13px] font-semibold text-brand-ink">{label}</label>
            <div className="flex-1 relative">
                <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testid}
                    className="w-full px-3 py-2 rounded-md bg-white border border-brand-line text-base font-display focus:outline-none focus:border-brand-gold"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm pointer-events-none">€</span>
            </div>
        </div>
    );
}


// ---------------------------------------------------------------------------
// PROMOS PANEL — gestione codici sconto
// ---------------------------------------------------------------------------
function PromosPanel() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null); // null = list, "new" = create, "<id>" = edit
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/promos");
            setList(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const del = async (id, code) => {
        if (!window.confirm(`Eliminare la promo ${code}?`)) return;
        try {
            await api.delete(`/admin/promos/${id}`);
            toast.success("Promo eliminata");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    if (editing) {
        const target = editing === "new" ? null : list.find((p) => p.id === editing);
        return (
            <PromoEditor
                promo={target}
                onCancel={() => setEditing(null)}
                onSaved={async () => { setEditing(null); await load(); }}
            />
        );
    }

    return (
        <div className="space-y-5" data-testid="admin-promos-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display text-3xl text-brand-ink">Codici promo</h2>
                    <p className="text-[13px] text-brand-muted mt-1">
                        Promo a scadenza o a numero massimo di utilizzi. Si applicano al checkout Stripe.
                    </p>
                </div>
                <button
                    onClick={() => setEditing("new")}
                    data-testid="promo-new-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-5 py-2.5 text-sm font-bold hover:bg-brand-goldDark transition shadow-gold"
                >
                    <Plus size={14} /> Nuova promo
                </button>
            </div>

            {loading ? (
                <div className="text-sm text-brand-muted">Caricamento…</div>
            ) : list.length === 0 ? (
                <div className="rounded-2xl border border-brand-line bg-paper p-10 text-center">
                    <Tag size={28} className="mx-auto text-brand-muted mb-3" />
                    <div className="font-display text-xl text-brand-ink">Nessuna promo attiva</div>
                    <p className="text-[13px] text-brand-muted mt-1">
                        Crea il primo codice per offrire sconti ai tuoi utenti.
                    </p>
                </div>
            ) : (
                <div className="rounded-2xl border border-brand-line bg-paper shadow-card overflow-hidden">
                    <table className="w-full text-sm" data-testid="promos-table">
                        <thead className="bg-brand-cream/60 border-b border-brand-line">
                            <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-brand-muted font-bold">
                                <th className="px-4 py-3">Codice</th>
                                <th className="px-4 py-3">Sconto</th>
                                <th className="px-4 py-3">Piano</th>
                                <th className="px-4 py-3">Scadenza</th>
                                <th className="px-4 py-3">Usi</th>
                                <th className="px-4 py-3">Stato</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((p) => (
                                <tr key={p.id} className="border-t border-brand-line">
                                    <td className="px-4 py-3">
                                        <div className="font-display text-base font-bold text-brand-navy">{p.code}</div>
                                        {p.description && (
                                            <div className="text-[11px] text-brand-muted">{p.description}</div>
                                        )}
                                        {p.public && (
                                            <span className="ml-0.5 inline-block mt-0.5 text-[9px] uppercase font-bold text-emerald-700">pubblica</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 font-bold text-brand-gold">-{p.discount_pct}%</td>
                                    <td className="px-4 py-3">
                                        {p.applies_to === "all" ? "Tutti" :
                                         p.applies_to === "base" ? "Base" :
                                         p.applies_to === "pro" ? "Pro" : "Top-up"}
                                    </td>
                                    <td className="px-4 py-3 text-[12px] text-brand-ink/80">
                                        {p.valid_until ? new Date(p.valid_until).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-[12px]">
                                        {p.used_count}{p.max_uses > 0 ? ` / ${p.max_uses}` : ""}
                                    </td>
                                    <td className="px-4 py-3">
                                        {p.is_active_now ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                                                Attiva
                                            </span>
                                        ) : !p.active ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-brand-muted bg-brand-cream border border-brand-line rounded-full px-2 py-0.5">
                                                Disattivata
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                                Scaduta
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={() => setEditing(p.id)}
                                                className="text-[12px] text-brand-navy hover:text-brand-gold font-semibold"
                                                data-testid={`promo-edit-${p.code}`}
                                            >
                                                Modifica
                                            </button>
                                            <span className="text-brand-line">·</span>
                                            <button
                                                onClick={() => del(p.id, p.code)}
                                                className="text-[12px] text-brand-danger hover:text-red-700"
                                                data-testid={`promo-delete-${p.code}`}
                                            >
                                                Elimina
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function PromoEditor({ promo, onCancel, onSaved }) {
    const isNew = !promo;
    const [form, setForm] = useState(() => ({
        code: promo?.code || "",
        description: promo?.description || "",
        notes: promo?.notes || "",
        discount_pct: promo?.discount_pct || 10,
        applies_to: promo?.applies_to || "all",
        valid_from: promo?.valid_from ? promo.valid_from.slice(0, 16) : "",
        valid_until: promo?.valid_until ? promo.valid_until.slice(0, 16) : "",
        max_uses: promo?.max_uses || 0,
        active: promo?.active ?? true,
        public: promo?.public ?? false,
    }));
    const [busy, setBusy] = useState(false);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.code.trim()) {
            toast.error("Inserisci un codice");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                code: form.code.trim().toUpperCase(),
                discount_pct: parseFloat(form.discount_pct) || 0,
                max_uses: parseInt(form.max_uses) || 0,
                valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
                valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
            };
            if (isNew) {
                await api.post("/admin/promos", payload);
                toast.success(`Promo ${payload.code} creata`);
            } else {
                await api.put(`/admin/promos/${promo.id}`, payload);
                toast.success(`Promo ${payload.code} aggiornata`);
            }
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-3xl space-y-6" data-testid="promo-editor">
            <div className="flex items-center gap-3">
                <button
                    onClick={onCancel}
                    className="text-[13px] text-brand-muted hover:text-brand-ink"
                >
                    ← Torna alla lista
                </button>
            </div>
            <h2 className="font-display text-3xl text-brand-ink">
                {isNew ? "Nuova promo" : `Modifica ${promo.code}`}
            </h2>
            <div className="rounded-2xl border border-brand-line bg-paper shadow-card p-6 space-y-5">
                <FormRow label="Codice">
                    <input
                        type="text"
                        value={form.code}
                        onChange={(e) => upd("code", e.target.value.toUpperCase())}
                        placeholder="ES: BLACKFRIDAY"
                        data-testid="promo-form-code"
                        className="w-full px-3 py-2 rounded-md bg-white border border-brand-line text-base font-display tracking-wider focus:outline-none focus:border-brand-gold"
                    />
                </FormRow>
                <FormRow label="Descrizione">
                    <input
                        type="text"
                        value={form.description}
                        onChange={(e) => upd("description", e.target.value)}
                        placeholder="-30% sul Pro per il Black Friday"
                        data-testid="promo-form-desc"
                        className="w-full px-3 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                    />
                </FormRow>
                <FormRow label="Note pubbliche" hint="Visibili al cliente nel banner e nel dialog dopo la validazione del codice. Es. termini, esclusioni, durata reale.">
                    <textarea
                        value={form.notes}
                        onChange={(e) => upd("notes", e.target.value)}
                        placeholder="Es. Promo riservata ai nuovi iscritti, non cumulabile con altre offerte. Lo sconto si applica solo sul primo mese di abbonamento."
                        data-testid="promo-form-notes"
                        rows={3}
                        className="w-full px-3 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold resize-y"
                    />
                </FormRow>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormRow label="Sconto %" stacked>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={form.discount_pct}
                            onChange={(e) => upd("discount_pct", e.target.value)}
                            data-testid="promo-form-pct"
                            className="w-full px-3 py-2 rounded-md bg-white border border-brand-line text-base font-display focus:outline-none focus:border-brand-gold"
                        />
                    </FormRow>
                    <FormRow label="Si applica a" stacked>
                        <select
                            value={form.applies_to}
                            onChange={(e) => upd("applies_to", e.target.value)}
                            data-testid="promo-form-applies"
                            className="w-full px-3 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        >
                            <option value="all">Tutti i piani</option>
                            <option value="base">Solo Base</option>
                            <option value="pro">Solo Pro</option>
                            <option value="topup">Solo Top-up</option>
                        </select>
                    </FormRow>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormRow label="Valido dal" stacked>
                        <input
                            type="datetime-local"
                            value={form.valid_from}
                            onChange={(e) => upd("valid_from", e.target.value)}
                            data-testid="promo-form-from"
                            className="w-full px-3 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                    </FormRow>
                    <FormRow label="Scadenza" stacked>
                        <input
                            type="datetime-local"
                            value={form.valid_until}
                            onChange={(e) => upd("valid_until", e.target.value)}
                            data-testid="promo-form-until"
                            className="w-full px-3 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                    </FormRow>
                </div>
                <FormRow label="Max utilizzi" hint="0 = illimitati">
                    <input
                        type="number"
                        min="0"
                        value={form.max_uses}
                        onChange={(e) => upd("max_uses", e.target.value)}
                        data-testid="promo-form-max"
                        className="w-40 px-3 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                    />
                </FormRow>
                <div className="flex flex-col gap-2 pt-2 border-t border-brand-line">
                    <label className="flex items-center gap-2 text-[13px]">
                        <input
                            type="checkbox"
                            checked={form.active}
                            onChange={(e) => upd("active", e.target.checked)}
                            data-testid="promo-form-active"
                        />
                        <span>Attiva</span>
                    </label>
                    <label className="flex items-center gap-2 text-[13px]">
                        <input
                            type="checkbox"
                            checked={form.public}
                            onChange={(e) => upd("public", e.target.checked)}
                            data-testid="promo-form-public"
                        />
                        <span>Mostra pubblicamente nella pagina Billing degli utenti</span>
                    </label>
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t border-brand-line">
                    <button
                        onClick={onCancel}
                        className="px-5 py-2.5 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                    >
                        Annulla
                    </button>
                    <button
                        onClick={save}
                        disabled={busy}
                        data-testid="promo-form-save"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-6 py-2.5 text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-50"
                    >
                        <Save size={14} /> {isNew ? "Crea promo" : "Salva"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FormRow({ label, hint, stacked, children }) {
    if (stacked) {
        return (
            <div>
                <label className="block text-[11px] uppercase tracking-[0.14em] text-brand-muted font-bold mb-1">{label}</label>
                {children}
                {hint && <div className="text-[11px] text-brand-muted mt-1">{hint}</div>}
            </div>
        );
    }
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="sm:w-40 text-[11px] uppercase tracking-[0.14em] text-brand-muted font-bold">{label}</label>
            <div className="flex-1">
                {children}
                {hint && <div className="text-[11px] text-brand-muted mt-1">{hint}</div>}
            </div>
        </div>
    );
}
